import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const CLOCK_OPTIONS = [
  { id: 'reset', label: 'Reset Match', key: 'R' },
  { id: 'divider1', type: 'divider' },
  { id: 'startFirstHalf', label: 'Start clock (first half)', key: '1' },
  { id: 'endFirstHalf', label: 'End clock (first half)', key: 'A' },
  { id: 'startSecondHalf', label: 'Start clock (second half)', key: '2' },
  { id: 'endSecondHalf', label: 'End clock (second half)', key: 'B' },
  { id: 'divider2', type: 'divider' },
  { id: 'overwrite', label: 'Overwrite clock', key: 'O' },
];

const ClockOptionsDialog = ({ open, onClose, onSelect, defaultFocusedOptionId }) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listItemRefs = useRef([]);

  // Set focus to the next logical option when dialog opens
  useEffect(() => {
    if (open) {
      const idx = defaultFocusedOptionId
        ? CLOCK_OPTIONS.findIndex(opt => opt.id === defaultFocusedOptionId)
        : -1;
      const firstNonDivider = CLOCK_OPTIONS.findIndex(opt => opt.type !== 'divider');
      setFocusedIndex(idx >= 0 ? idx : firstNonDivider);
    }
  }, [open, defaultFocusedOptionId]);

  const handleSelect = useCallback((option) => {
    if (option.type === 'divider') return;
    onSelect(option);
    onClose();
  }, [onSelect, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const validOptions = CLOCK_OPTIONS.filter(opt => opt.type !== 'divider');
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const currentValidIndex = validOptions.findIndex(opt => opt.id === CLOCK_OPTIONS[prev]?.id);
            const nextValidIndex = Math.min(currentValidIndex + 1, validOptions.length - 1);
            return CLOCK_OPTIONS.findIndex(opt => opt.id === validOptions[nextValidIndex]?.id);
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const currentValidIndex = validOptions.findIndex(opt => opt.id === CLOCK_OPTIONS[prev]?.id);
            const nextValidIndex = Math.max(currentValidIndex - 1, 0);
            return CLOCK_OPTIONS.findIndex(opt => opt.id === validOptions[nextValidIndex]?.id);
          });
          break;
        case 'Enter':
          event.preventDefault();
          const selectedOption = CLOCK_OPTIONS[focusedIndex];
          if (selectedOption && selectedOption.type !== 'divider') {
            handleSelect(selectedOption);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          // Check for shortcut keys
          const matchedOption = CLOCK_OPTIONS.find(opt => opt.key && opt.key.toLowerCase() === event.key.toLowerCase());
          if (matchedOption) {
            event.preventDefault();
            handleSelect(matchedOption);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, focusedIndex, handleSelect, onClose]);

  // Scroll focused item into view
  useEffect(() => {
    if (listItemRefs.current[focusedIndex]) {
      listItemRefs.current[focusedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [focusedIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#1E1E1E',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          width: '480px',
          maxHeight: '500px',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          pb: 1,
          pt: 2,
          px: 2.5,
        }}
      >
        Select Option
      </DialogTitle>
      <DialogContent
        sx={{
          px: 2,
          py: 1.5,
          '&.MuiDialogContent-root': {
            paddingTop: 1.5,
          },
        }}
      >
        <List
          sx={{
            py: 0,
            maxHeight: '350px',
            overflowY: 'auto',
          }}
        >
          {CLOCK_OPTIONS.map((option, index) => {
            if (option.type === 'divider') {
              return <Divider key={option.id} sx={{ my: 0.5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />;
            }

            const isSelected = index === focusedIndex;
            return (
              <ListItem
                key={option.id}
                disablePadding
                ref={(el) => (listItemRefs.current[index] = el)}
              >
                <ListItemButton
                  onClick={() => handleSelect(option)}
                  selected={isSelected}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    py: 1,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(69, 255, 213, 0.15)',
                      border: '1px solid rgba(69, 255, 213, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(69, 255, 213, 0.2)',
                      },
                    },
                  }}
                >
                  {option.key && (
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        mr: 1.5,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#FFFFFF',
                        flexShrink: 0,
                      }}
                    >
                      {option.key}
                    </Box>
                  )}
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      sx: {
                        color: '#FFFFFF',
                        fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.4,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#FFFFFF',
            fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ClockOptionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  defaultFocusedOptionId: PropTypes.string,
};

export default ClockOptionsDialog;
